/**
 * Digital Detox utility functions
 */

import { parseISO, startOfDay, isBefore, isAfter, isWithinInterval, format, isValid } from 'date-fns';

export type DetoxStatus = 'cancelled' | 'scheduled' | 'active' | 'completed';

/**
 * Compute the status of a detox session based on dates and stored status
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param endDate - End date string (YYYY-MM-DD)
 * @param storedStatus - Stored status from backend (only 'cancelled' is manually set)
 * @returns Computed status
 */
export function computeStatus(
  startDate: string,
  endDate: string,
  storedStatus?: string | null
): DetoxStatus {
  // If manually cancelled, return cancelled
  if (storedStatus === 'cancelled') {
    return 'cancelled';
  }

  const today = startOfDay(new Date());
  const start = startOfDay(parseISO(startDate));
  const end = startOfDay(parseISO(endDate));

  // Completed: end date is before today
  if (isBefore(end, today)) {
    return 'completed';
  }

  // Active: today is between start and end (inclusive)
  if (isWithinInterval(today, { start, end })) {
    return 'active';
  }

  // Scheduled: start date is after today
  if (isAfter(start, today)) {
    return 'scheduled';
  }

  // Default to scheduled if dates are invalid
  return 'scheduled';
}

/**
 * Format a Date object to YYYY-MM-DD string for HTML date input
 * @param date - Date object or string
 * @returns YYYY-MM-DD formatted string
 */
export function formatDateForInput(date: Date | string | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    
    return format(dateObj, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

/**
 * Parse YYYY-MM-DD string from HTML date input to Date object
 * @param dateString - YYYY-MM-DD formatted string
 * @returns Date object or null if invalid
 */
export function parseDateFromInput(dateString: string): Date | null {
  if (!dateString) return null;
  
  try {
    const date = parseISO(dateString);
    if (!isValid(date)) return null;
    
    return date;
  } catch {
    return null;
  }
}

export function formatDateDisplay(date: Date | string | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    
    return format(dateObj, 'dd/MM/yyyy');
  } catch {
    return '';
  }
}

export function getTodayString(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function buildDetoxSessionsWhereClause(
  statusFilter: DetoxStatus | 'all',
  dateFilter?: string | null,
  startDateFilter?: string | null,
  endDateFilter?: string | null
): any {
  const whereClause: any = {
    is_deleted: { _eq: false },
  };

  // Add status filter if not 'all'
  if (statusFilter !== 'all') {
    whereClause.status = { _eq: statusFilter };
  }

  // Add date range filters
  if (startDateFilter) {
    whereClause.start_date = { _gte: startDateFilter };
  }
  if (endDateFilter) {
    whereClause.end_date = { _lte: endDateFilter };
  }

  // Add single date filter (sessions that contain this date)
  // This checks that start_date <= date AND end_date >= date
  if (dateFilter) {
    whereClause._and = [
      {
        start_date: { _lte: dateFilter },
        end_date: { _gte: dateFilter },
      },
    ];
  }

  return whereClause;
}

/**
 * Map GraphQL/PostgreSQL errors to user-friendly messages
 * @param error - Error object from GraphQL
 * @returns User-friendly error message
 */
export function mapGraphQLError(error: any): string {
  if (!error) return 'An unknown error occurred';
  
  const errorMessage = error.message || String(error);
  
  // PostgreSQL constraint violation errors
  if (errorMessage.includes('23505') || errorMessage.includes('unique constraint') || errorMessage.includes('exclusion constraint')) {
    return 'A detox session already exists for this date range.';
  }
  
  if (errorMessage.includes('23514') || errorMessage.includes('check constraint')) {
    return 'End date must be the same as or after start date.';
  }
  
  // Return the original error message if no mapping found
  return errorMessage;
}


